import { useState, useEffect, useRef } from "react";
import { Upload, X, User, IdCard, Key, ChevronDown } from "lucide-react";
import Swal from "sweetalert2";
import { WorkerForm } from "../../../../types/workpermit";

interface WorkerModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: "add" | "edit";
  worker: WorkerForm;
  onSave: (worker: WorkerForm) => void;
}

export default function WorkerModal({
  isOpen,
  onClose,
  mode,
  worker,
  onSave,
}: WorkerModalProps) {
  const [name, setName] = useState("");
  const [ktp, setKtp] = useState("");
  const [accessCard, setAccessCard] = useState(0);
  const [parkingKey, setParkingKey] = useState(0);
  const [imgPreview, setImgPreview] = useState("");
  const [fileName, setFileName] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && worker) {
      setName(worker.PersonnelName || "");
      setKtp(worker.IdentityNumber || "");
      setAccessCard(worker.IsAccessCard ?? 0);
      setParkingKey(worker.IsParkingLotKey || 0);
      setImgPreview(worker.DataImage || "");
      setFileName(worker.DataImage ? "uploadfilektp.jpg" : "");
    }
  }, [isOpen, worker, mode]);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setFileName(files[0].name);

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setImgPreview(result);
    };
    reader.readAsDataURL(files[0]);
  };

  const handleSave = () => {
    if (!name.trim()) {
      Swal.fire({ title: "Validation error", text: "Nama pekerja harus diisi", icon: "error", confirmButtonColor: "#2563eb" });
      return;
    }
    if (ktp.length !== 16 || !/^\d+$/.test(ktp)) {
      Swal.fire({ title: "Validation error", text: "Nomor KTP harus 16 digit angka", icon: "error", confirmButtonColor: "#2563eb" });
      return;
    }
    if (!imgPreview) {
      Swal.fire({ title: "Validation error", text: "Foto KTP harus diupload", icon: "error", confirmButtonColor: "#2563eb" });
      return;
    }

    onSave({
      PersonnelName: name.trim(),
      IdentityNumber: ktp.trim(),
      DataImage: imgPreview,
      ImageLabel: "Foto KTP",
      IsAccessCard: accessCard,
      IsParkingLotKey: parkingKey,
    });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center p-4 z-50">
      <div
        className="absolute inset-0 bg-black/45 backdrop-blur-sm transition-all duration-300"
        onClick={onClose}
      />
      <div className="relative bg-white dark:bg-gray-800 rounded-3xl w-full max-w-md shadow-2xl z-10 border border-gray-100 dark:border-gray-700 overflow-hidden transform transition-all duration-300">
        <div className="relative px-6 py-5 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-extrabold text-gray-900 dark:text-white">
              {mode === "add" ? "Tambah Pekerja" : "Edit Pekerja"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 rounded-full transition"
          >
            <X className="w-5 h-5 stroke-[2.5]" />
          </button>
        </div>

        <div className="px-6 py-2 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block text-xs font-bold text-slate-400 dark:text-slate-500 mb-1.5 uppercase tracking-wide">
              Nama Pekerja
            </label>
            <div className="relative flex items-center border border-gray-200 dark:border-gray-600 rounded-xl px-3.5 py-2.5 bg-white dark:bg-gray-750 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 transition-all">
              <User className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nama Pekerja"
                className="w-full bg-transparent border-0 p-0 text-sm focus:ring-0 focus:outline-none dark:text-white font-semibold"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-400 dark:text-slate-500 mb-1.5 uppercase tracking-wide">
              Nomor KTP
            </label>
            <div className="relative flex items-center border border-gray-200 dark:border-gray-600 rounded-xl px-3.5 py-2.5 bg-white dark:bg-gray-750 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 transition-all">
              <IdCard className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
              <input
                type="text"
                value={ktp}
                onChange={(e) => setKtp(e.target.value)}
                maxLength={16}
                placeholder="Nomor KTP (16 digit)"
                className="w-full bg-transparent border-0 p-0 text-sm focus:ring-0 focus:outline-none dark:text-white font-semibold font-mono"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-400 dark:text-slate-500 mb-1.5 uppercase tracking-wide">
              Upload Foto KTP
            </label>
            <div
              onClick={() => fileRef.current?.click()}
              className="relative flex items-center border border-gray-200 dark:border-gray-600 rounded-xl px-3.5 py-2.5 bg-white dark:bg-gray-750 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 transition-all cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Upload className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
              <input
                type="file"
                ref={fileRef}
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300 font-semibold truncate">
                {fileName || "Klik untuk upload foto KTP"}
              </span>
            </div>

            {imgPreview && (
              <div className="mt-3 relative rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-slate-50 dark:bg-gray-900/50 p-2 flex justify-center items-center">
                <img
                  src={imgPreview}
                  alt="ID Card Preview"
                  className="max-h-40 w-full object-contain rounded-lg shadow-xs"
                />
                <button
                  type="button"
                  onClick={() => {
                    setImgPreview("");
                    setFileName("");
                    if (fileRef.current) {
                      fileRef.current.value = "";
                    }
                  }}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full transition shadow-md flex items-center justify-center"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-400 dark:text-slate-500 mb-1.5 uppercase tracking-wide">
              Kartu Akses
            </label>
            <div className="relative flex items-center border border-gray-200 dark:border-gray-600 rounded-xl px-3.5 py-2.5 bg-white dark:bg-gray-750 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 transition-all">
              <IdCard className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
              <select
                value={accessCard}
                onChange={(e) => setAccessCard(Number(e.target.value))}
                className="w-full bg-transparent border-0 p-0 text-sm focus:ring-0 focus:outline-none dark:text-white font-semibold appearance-none pr-8"
              >
                <option value={1}>Ya</option>
                <option value={0}>Tidak</option>
              </select>
              <ChevronDown className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-400 dark:text-slate-500 mb-1.5 uppercase tracking-wide">
              Kunci Parkir
            </label>
            <div className="relative flex items-center border border-gray-200 dark:border-gray-600 rounded-xl px-3.5 py-2.5 bg-white dark:bg-gray-750 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 transition-all">
              <Key className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
              <select
                value={parkingKey}
                onChange={(e) => setParkingKey(Number(e.target.value))}
                className="w-full bg-transparent border-0 p-0 text-sm focus:ring-0 focus:outline-none dark:text-white font-semibold appearance-none pr-8"
              >
                <option value={1}>Ya</option>
                <option value={0}>Tidak</option>
              </select>
              <ChevronDown className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            </div>
          </div>
        </div>

        <div className="px-6 py-6 flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            className="px-8 py-2.5 text-xs font-extrabold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-all shadow-sm active:scale-98"
          >
            {mode === "add" ? "Tambah" : "Simpan"}
          </button>
        </div>
      </div>
    </div>
  );
}
